/**
 * URL Tracker - Captures and stores URL query string from advertising campaigns
 * This ensures we capture parameters even if the user doesn't complete the form
 */

import { getSessionId } from './supabase';
import { logger } from './utils';

// Key for storing URL parameters in localStorage
const URL_QUERY_STRING_KEY = 'adressfinder_url_query_string';
const FIRST_VISIT_TIME_KEY = 'adressfinder_first_visit_time';

/**
 * Captures the URL query string (everything after the question mark)
 * and stores it in localStorage for later use
 */
export const captureUrlParameters = async (): Promise<void> => {
  try {
    // Get the query string (everything after the question mark)
    const queryString = window.location.search;

    // Only proceed if we have parameters
    if (!queryString || queryString.length <= 1) { // Just the "?" or nothing
      return;
    }

    // Check if this is the first visit (no parameters stored yet)
    const existingQueryString = localStorage.getItem(URL_QUERY_STRING_KEY);

    // Parse the existing and new query strings
    const existingParams = existingQueryString ? new URLSearchParams(existingQueryString) : new URLSearchParams();
    const newParams = new URLSearchParams(queryString);

    // IMPORTANT: Preserve existing parameters if they contain a Google click ID and new parameters don't
    // This ensures Google Ads tracking is not lost during navigation
    // gclid = standard web clicks, wbraid = iOS web clicks (Safari ITP), gbraid = iOS app clicks
    const existingHasClickId = existingParams.has('gclid') || existingParams.has('wbraid') || existingParams.has('gbraid');
    const newHasClickId = newParams.has('gclid') || newParams.has('wbraid') || newParams.has('gbraid');

    if (existingHasClickId && !newHasClickId) {
      logger.log('Preserving existing parameters with click ID - new parameters do not contain click ID');
      // Don't merge or update - keep existing parameters including click ID
      return;
    }

    // Merge the parameters, with new ones taking precedence
    let paramsUpdated = false;
    newParams.forEach((value, key) => {
      // Only update if the parameter doesn't exist or if it's a session_id (which we always want to update)
      if (!existingParams.has(key) || key === 'session_id') {
        existingParams.set(key, value);
        paramsUpdated = true;
      }
    });

    // If we updated any parameters, store the merged query string
    if (paramsUpdated || !existingQueryString) {
      const mergedQueryString = `?${existingParams.toString()}`;
      localStorage.setItem(URL_QUERY_STRING_KEY, mergedQueryString);

      if (!existingQueryString) {
        localStorage.setItem(FIRST_VISIT_TIME_KEY, new Date().toISOString());
      }

      // Also store individual UTM parameters for easier access
      if (newParams.get('utm_source')) localStorage.setItem('utm_source', newParams.get('utm_source')!);
      if (newParams.get('utm_medium')) localStorage.setItem('utm_medium', newParams.get('utm_medium')!);
      if (newParams.get('utm_campaign')) localStorage.setItem('utm_campaign', newParams.get('utm_campaign')!);
      if (newParams.get('utm_content')) localStorage.setItem('utm_content', newParams.get('utm_content')!);
      if (newParams.get('utm_term')) localStorage.setItem('utm_term', newParams.get('utm_term')!);

      // Always update the user_sessions table with the merged URL query string
      await updateUserSessionWithQueryString(mergedQueryString);
    }
  } catch (error) {
    logger.error('Error capturing URL parameters:', error);
  }
};

/**
 * Updates the user_sessions table with the URL query string via Edge Function
 * @param queryString The URL query string to store
 */
export const updateUserSessionWithQueryString = async (queryString: string): Promise<void> => {
  try {
    const sessionId = await getSessionId();

    const urlParams = new URLSearchParams(queryString);
    const utmSource = urlParams.get('utm_source') || undefined;
    const utmMedium = urlParams.get('utm_medium') || undefined;
    const utmCampaign = urlParams.get('utm_campaign') || undefined;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(`${supabaseUrl}/functions/v1/update-session-query-string`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        session_id: sessionId,
        query_string: queryString,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      logger.error('Error updating user session with URL query string:', errorData);
    }
  } catch (error) {
    logger.error('Error updating user session with URL query string:', error);
  }
};
